/**
 * Checks whether or not `localStorage`/`sessionStorage` is available.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Testing_for_availability}
 * @see {@link https://gist.github.com/paulirish/5558557}
 *
 * @since 0.1.13
 *
 * @param   {string} type The storage type. Either `localStorage` or `sessionStorage`.
 * @returns {boolean}     Is `localStorage`/`sessionStorage` available or not?
 */
export const storageAvailable = (type) => {
	try {
		let storage = window[type]
		let x = '__storage_test__'
		storage.setItem(x, x)
		storage.removeItem(x)
		return true
	} catch (e) {
		return e instanceof DOMException && (
			// everything except Firefox
			22 === e.code ||
			// Firefox
			1014 === e.code ||
			// test name field too, because code might not be present
			// everything except Firefox
			'QuotaExceededError' === e.name ||
			// Firefox
			'NS_ERROR_DOM_QUOTA_REACHED' === e.name) &&
			// acknowledge QuotaExceededError only if there's something already stored
			0 === window[type].length
	}
}

const getStorage = type => {
	const storage = `${type}Storage`;
	return {
		shouldThrow: false,
		getItem : key => {
			try {
				if (storageAvailable(storage)) {
					const value = window[storage].getItem(key);
					try {
						return JSON.parse(value)
					} catch(e) {
						return value;
					}
				}
			} catch (e) {
				if (this.shouldThrow) {
					throw e;
				}
			}

			return null
		},
		setItem : (key, value) => {
			try {
				if (storageAvailable(storage)) {
					return window[storage].setItem(key, JSON.stringify(value))
				}
			} catch (e) {
				if (this.shouldThrow) {
					throw e;
				}
			}

			return null
		},
		removeItem : key => {
			try {
				if (storageAvailable(storage)) {
					return window[storage].removeItem(key)
				}
			} catch (e) {
				if (this.shouldThrow) {
					throw e;
				}
			}

			return null
		},
	}
}

export const session = getStorage('session')
export const local = getStorage('local')
